import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareTextEditor } from '@/components/form/formAwareInputs/FormAwareTextEditor';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormProvider, useForm } from '@/components/react-hook-form';

interface AdditionalPromptContextFormShape {
  additionalPromptContext: string;
}

export interface AdditionalPromptContextModalProps {
  initialValue: string;
  onClose: () => void;
  onSave: (value: string | null) => void;
}

/**
 * Edits the firm's instructions that are appended to the built-in AI prompt
 * for one template slide. Mount it only while open so the draft resets to the
 * saved value each time it opens.
 */
export function AdditionalPromptContextModal({
  initialValue,
  onClose,
  onSave,
}: AdditionalPromptContextModalProps) {
  const form = useForm<AdditionalPromptContextFormShape>({
    defaultValues: { additionalPromptContext: initialValue },
  });

  const handleSave = useCallback(() => {
    const trimmed = form.getValues().additionalPromptContext.trim();
    onSave(trimmed === '' ? null : trimmed);
  }, [form, onSave]);

  const actions = useMemo(
    () => (
      <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    ),
    [onClose, handleSave]
  );

  return (
    <FormProvider {...form}>
      <FormModal
        heading="Additional prompt context"
        isOpen
        onClose={onClose}
        actions={actions}
        rightHeaderContent={null}
      >
        <Stack direction="column" spacing={2}>
          <Typography variant="body-s">
            Added to the built-in prompt when this slide is generated. Use it to
            add instructions or to change the default behavior.
          </Typography>
          <FormAwareTextEditor<AdditionalPromptContextFormShape>
            control={form.control}
            fieldName="additionalPromptContext"
            label="Instructions"
            rows={12}
            omitBlockTypes={['h1', 'h2', 'h3', 'quote']}
            allowTableInsert={false}
            preserveNewLines
            autoFocus
          />
        </Stack>
      </FormModal>
    </FormProvider>
  );
}
